import React, { useEffect, useState } from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ShieldCheck } from "lucide-react";

/** Dispatcher-only: add a Square card on file for ANY customer by email —
 *  even customers who have never used a card before. Rendered inside the
 *  customer-profile drawer's Saved Cards tab. */
export default function DispatcherAddCard({ email, onSaved }) {
  const [adding, setAdding] = useState(false);
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (adding && methods === null) {
      api.get("/payments/methods").then(r => setMethods(r.data)).catch(() => setMethods({}));
    }
  }, [adding, methods]);

  const handleToken = async (token, verifiedBuyer) => {
    setBusy(true);
    try {
      await api.post("/payments/cards/save-for-email", {
        email,
        source_id: token.token,
        verification_token: verifiedBuyer?.token,
      });
      toast.success(`Card saved on file for ${email}`);
      setAdding(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save card");
    } finally { setBusy(false); }
  };

  if (!email) return null;

  if (!adding) {
    return (
      <button data-testid="dispatcher-add-card-btn" onClick={() => setAdding(true)}
              className="btn-ghost !py-2 !px-3 text-[10px]">
        <Plus size={12} className="inline mr-1.5"/>Add a card for this customer
      </button>
    );
  }

  if (methods === null) return <div className="text-xs text-[#71717A]">Loading secure card form…</div>;
  if (!methods?.card?.enabled) return <div className="text-xs text-[#A1A1AA]">Card storage isn&apos;t configured.</div>;

  return (
    <div className="border border-white/10 bg-black/30 p-4" data-testid="dispatcher-add-card-form">
      <div className="flex items-center gap-2 text-[11px] text-[#A1A1AA] mb-3">
        <ShieldCheck size={13} className="text-[#D4AF37]"/>
        Saving to <span className="text-[#F5F2EB]">{email}</span> · secured by Square
      </div>
      <PaymentForm
        applicationId={methods.card.app_id}
        locationId={methods.card.location_id}
        cardTokenizeResponseReceived={handleToken}
        createVerificationDetails={() => ({
          amount: "0.00", currencyCode: "USD", intent: "STORE", billingContact: {},
        })}
      >
        <CreditCard buttonProps={{ css: {
          backgroundColor: "#D4AF37", color: "#050505", fontFamily: "Outfit, sans-serif",
          fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", fontSize: "12px",
          "&:hover": { backgroundColor: "#C29B2E" },
        }}}>
          {busy ? "Saving…" : "Save card on file"}
        </CreditCard>
      </PaymentForm>
      <button data-testid="dispatcher-add-card-cancel" onClick={() => setAdding(false)}
              className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-[#F5F2EB]">
        Cancel
      </button>
    </div>
  );
}
